import * as React from "react";
import { I18n } from "react-redux-i18n";

import { Flex } from "@common/components/Flex";
import { Radio } from "@common/components/radio";
import { FormControl } from "@common/components/form-control";

import { getTestAttributes } from "@common/utils/tests";
import { MODULE_NAME } from "@modules/invite-status/constants/shared";

import { InvitesExportType } from "../invites-export.types";

import "./export-settings.styles.scss";

export type InvitesExportSettingsProps = {
  onChange: (value: { exportType: InvitesExportType; delimiter?: string }) => void;
  value: {
    exportType: InvitesExportType;
    delimiter?: string;
  };
};

export const InvitesExportSettings: React.FC<InvitesExportSettingsProps> = ({ onChange, value }) => {
  const settings = React.useMemo(() => {
    const config: Array<{ label: string; value: InvitesExportType }> = [
      { label: "Text (Tab Delimited)", value: InvitesExportType.tabDelimited },
      { label: "Text (Comma Delimited)", value: InvitesExportType.commaDelimited },
      { label: "Text (Other)", value: InvitesExportType.other },
      { label: "Excel (xls)", value: InvitesExportType.xls },
      { label: "Excel (xlsx)", value: InvitesExportType.xlsx },
    ];

    return config;
  }, []);

  const handleSettingChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, exportType: e.target.value as InvitesExportType });
    },
    [value, onChange]
  );

  const handleDelimiterChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ exportType: value?.exportType!, delimiter: e.target.value });
    },
    [value, onChange]
  );

  return (
    <div className="InvitesExportSettings">
      <h6 className="InvitesExportSettings__title">{I18n.t("Settings")}</h6>
      {settings.map((setting, settingIndex) => (
        <Flex justify="space-between" key={setting.value}>
          <Radio
            {...getTestAttributes(MODULE_NAME, `invites-export-setting-${settingIndex}`, "radio")}
            className="InvitesExportSettings__option"
            name="exportSetting"
            value={setting.value}
            checked={value.exportType === setting.value}
            onChange={handleSettingChange}
            classes={{
              label: "InvitesExportSettings__option__label",
              input: "InvitesExportSettings__option__input",
            }}
          >
            {I18n.t(setting.label)}
          </Radio>
          {value.exportType === InvitesExportType.other && setting.value === InvitesExportType.other && (
            <Flex alignItems="center" className="InvitesExportSettings__delimiter-field">
              <p className="InvitesExportSettings__delimiter-label">{I18n.t("Delimiter")}:</p>
              <FormControl
                {...getTestAttributes(MODULE_NAME, "invites-export-delimiter", "input")}
                className="InvitesExportSettings__delimiter"
                onChange={handleDelimiterChange}
                value={value.delimiter || ""}
              />
            </Flex>
          )}
        </Flex>
      ))}
    </div>
  );
};
